import {
  cleanValueFromExpression,
  ConditionType,
  Filter,
  getEmptyExpression,
  getEmptyFilterExpression,
  getRootOperator,
  isIngressExp,
  isNullOperator,
  OperatorType,
  shouldRenderWithFieldFilterBox,
  StringConditionType,
} from '@fabric-ui/components';
import { LogicalOperatorType } from './types';

type FilterOperand = NonNullable<Filter['and']>['operands'][number];
type FieldMap = Parameters<typeof cleanValueFromExpression>[1];

interface EmptyExpressionOptions {
  defaultEmptyFilter?: Filter;
  defaultField?: string | null;
  fieldMap: FieldMap;
  supportParser?: boolean;
}

interface DeleteOperandOptions extends Omit<EmptyExpressionOptions, 'fieldMap'> {
  canDeleteLast?: boolean;
  hasListTab?: boolean;
  level?: number;
}

interface AddInlineOperandOptions {
  fieldMap: FieldMap;
  level?: number;
}

const getOperands = (filter: Filter, operator: LogicalOperatorType) => filter[operator]?.operands ?? [];

const hasEmptyValueOperand = (filter: Filter) => {
  const operator = getRootOperator(filter);
  if (operator !== OperatorType.AND && operator !== OperatorType.OR) return false;
  return !!filter[operator]?.operands.some(operand => {
    const condition = operand.expression?.stringCondition;
    return condition && Object.values(condition).some(value => value === '');
  });
};

export const addGlobalOperand = (filter: Filter, operator: LogicalOperatorType, options: EmptyExpressionOptions): Filter => {
  const rootOperator = getRootOperator<LogicalOperatorType>(filter);
  const operands = getOperands(filter, rootOperator);
  const lastOperand = operands.at(-1);
  const newItem =
    lastOperand && (lastOperand.expression || lastOperand.and || lastOperand.or || isIngressExp(lastOperand))
      ? lastOperand
      : options.defaultEmptyFilter ||
        getEmptyExpression(
          options.defaultField,
          ConditionType.string,
          options.supportParser ? StringConditionType.EQUALS : undefined,
          options.fieldMap[options.defaultField!]
        );

  return { [operator]: { operands: [...operands, cleanValueFromExpression(newItem, options.fieldMap)] } };
};

export const toggleAllLogicalOperators = (filter: Filter, hasListTab = false, isRoot = true): Filter => {
  if (!isRoot && shouldRenderWithFieldFilterBox(filter, hasListTab) && !(hasListTab && hasEmptyValueOperand(filter))) return filter;

  const operator = getRootOperator(filter);
  if (operator !== OperatorType.AND && operator !== OperatorType.OR) return filter;
  const oppositeOperator = operator === OperatorType.AND ? OperatorType.OR : OperatorType.AND;

  return {
    [oppositeOperator]: {
      operands: getOperands(filter, operator).map(operand =>
        isIngressExp(operand) ? operand : toggleAllLogicalOperators(operand, hasListTab, false)
      ),
    },
  };
};

export const replaceOperandAt = (filter: Filter, operator: LogicalOperatorType, index: number, value: FilterOperand): Filter => ({
  [operator]: { operands: getOperands(filter, operator).map((operand, operandIndex) => (operandIndex === index ? value : operand)) },
});

export const deleteOperandAt = (
  filter: Filter,
  operator: LogicalOperatorType,
  index: number,
  options: DeleteOperandOptions = {}
): Filter | undefined => {
  const operands = getOperands(filter, operator).filter((_, operandIndex) => operandIndex !== index);
  const level = options.level ?? 0;

  if (operands.length === 0 && level === 0) {
    if (options.canDeleteLast) return undefined;
    if (options.defaultEmptyFilter) return { and: { operands: [options.defaultEmptyFilter] } };
    return getEmptyFilterExpression(options.defaultField, options.supportParser ? StringConditionType.EQUALS : undefined);
  }

  if (operands.length === 1 && (level !== 0 || !operands[0].expression)) {
    if (level === 0 && shouldRenderWithFieldFilterBox(operands[0], options.hasListTab ?? false) && !operands[0].expression) {
      return { [operator]: { operands } };
    }
    return operands[0];
  }

  return { [operator]: { operands } };
};

export const addInlineOperand = (
  filter: Filter,
  operator: LogicalOperatorType,
  index: number,
  options: AddInlineOperandOptions
): Filter => {
  const operands = getOperands(filter, operator);
  const operand = operands[index];
  const cleaned = isIngressExp(operand) ? operand : cleanValueFromExpression(operand, options.fieldMap);
  const condition = cleaned.expression?.stringCondition;
  const newItem =
    condition && Object.keys(condition).some(isNullOperator)
      ? { expression: { fieldName: cleaned.expression?.fieldName, stringCondition: { equals: '' } } }
      : cleaned;

  if ((options.level ?? 0) === 0) {
    const oppositeOperator = operator === OperatorType.OR ? OperatorType.AND : OperatorType.OR;
    return { [operator]: { operands: operands.with(index, { [oppositeOperator]: { operands: [operand, newItem] } }) } };
  }

  return { [operator]: { operands: operands.toSpliced(index + 1, 0, newItem) } };
};

export const countExpressions = (filter: Filter): number =>
  (filter.expression ? 1 : 0) +
  (filter.and?.operands.reduce((total, operand) => total + countExpressions(operand), 0) ?? 0) +
  (filter.or?.operands.reduce((total, operand) => total + countExpressions(operand), 0) ?? 0);

export const getUniqueFieldNames = (filter: Filter, fieldNames = new Set<string>()): Set<string> => {
  const fieldName = filter.expression?.fieldName || filter.expression?.arrayCondition?.underlying.fieldName;
  if (fieldName) fieldNames.add(fieldName);
  filter.and?.operands.forEach(operand => getUniqueFieldNames(operand, fieldNames));
  filter.or?.operands.forEach(operand => getUniqueFieldNames(operand, fieldNames));
  return fieldNames;
};
